/**
 * Page de listing des opérations (Bons de Commande + Marchés Subséquents)
 * 
 * Ce composant affiche une liste d'opérations avec filtrage par onglets,
 * recherche et pagination.
 */

import './Operation.css';

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as ExportIcon } from '../../assets/icons/export.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { useAuth } from "../../contexts/AuthContext";
import { isClientRole } from '../../utils/roleUtils';
import { useNotifications } from "../../contexts/NotificationsContextType";
import { handleExportCSV } from '../../utils/commonHandlers';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import SearchBar from '../../composants/SearchBar/SearchBar';
import Button from '../../composants/Button/Button';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import TabNav from '../../composants/TabNav/TabNav';
import FiltreDeCommande from '../../composants/FiltreDeCommande/FiltreDeCommande';
import LancerMarcheModal from '../../composants/LancerMarcheModal/LancerMarcheModal';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import { formatDate, formatPrice } from '../../utils/entityUtils';

// Types et configuration
import { Operation, ApiOperation, ApiStatus, FilterOptions, PaginationState } from './types';
import { TABS, TAB_TO_STATUS_MAPPING, TYPE_OPTIONS } from './statusConfig';
import {
  transformApiOperationsToDisplay,
  transformBciOperationToDisplay,
  getStatusActionForRole,
  getUserRoleFromStorage,
  toCssClass,
  sortPaniersImportesByPriority,
  formatDateFR
} from './utils';
import {
  transformFiltersToApiParams,
  buildQueryString,
  ApiOperationFilters
} from './api';

// ============================================================================
// Types Props du composant
// ============================================================================

interface OperationPageProps {
  onNotificationUpdate?: (count: number) => void;
}

// ============================================================================
// Composant Principal
// ============================================================================

function OperationPage({ onNotificationUpdate }: OperationPageProps) {
  const { user } = useAuth();
  const isClientView = isClientRole(user?.role || '');
  const navigate = useNavigate();
  const { setUnreadOperations } = useNotifications();
  const abortControllerRef = useRef<AbortController | null>(null);

  // ============================================================================
  // États
  // ============================================================================

  // Modaux
  const [isLancerMarcheModalOpen, setIsLancerMarcheModalOpen] = useState(false);
  const [isBonCommandeModalOpen, setIsBonCommandeModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [operationToRefuse, setOperationToRefuse] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState<string | null>(null);
  const [operationToDeleteSource, setOperationToDeleteSource] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Données
  const [operations, setOperations] = useState<Operation[]>([]);
  const [filteredData, setFilteredData] = useState<Operation[]>([]);
  const [dataArchive, setDataArchive] = useState<any[]>([]);

  // Navigation et filtres
  const [activeTab, setActiveTab] = useState(() => TABS.indexOf('Tous'));
  const [selectedType, setSelectedType] = useState<string>(TYPE_OPTIONS[0].value);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [activeFilters, setActiveFilters] = useState<Partial<FilterOptions>>({});

  // États de chargement
  const [loading, setLoading] = useState<boolean>(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compteurs
  const [actionRequiseCount, setActionRequiseCount] = useState<number>(0);
  const [paniersImportesCount, setPaniersImportesCount] = useState<number>(0);
  const [unreadLocalOperations, setUnreadLocalOperations] = useState<number>(0);

  // Pagination
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    lastPage: 1,
    perPage: 50,
    total: 0
  });

  const itemsPerPage = 50;

  // ============================================================================
  // Fonctions de navigation
  // ============================================================================

  /**
   * Génère l'URL de base pour une opération selon son type et l'onglet actif
   */
  const getOperationBaseUrl = useCallback((operation: Operation): string => {
    const isPaniersImportes = TABS[activeTab] === 'Paniers importés';

    if (isPaniersImportes || operation.source === 'BCI') {
      return `/operation-bci/${operation.id}`;
    }
    return operation.source === 'BC'
      ? `/operation-bc/${operation.id}`
      : `/operation-ms/${operation.id}`;
  }, [activeTab]);

  const handleViewDetails = useCallback((id: string) => {
    const operation = operations.find(op => op.id === id);
    if (!operation) return;
    navigate(getOperationBaseUrl(operation));
  }, [operations, getOperationBaseUrl, navigate]);

  const handleCandidater = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    navigate(`${getOperationBaseUrl(operation)}?tab=1`, { state: { action: 'candidater' } });
  }, [operations, getOperationBaseUrl, navigate]);

  const handleAnalyser = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;

    const isPaniersImportes = TABS[activeTab] === 'Paniers importés';
    const tab = isPaniersImportes ? 1 : (operation.source === 'BC' ? 0 : 2);

    navigate(`${getOperationBaseUrl(operation)}?tab=${tab}`, { state: { action: 'analyser' } });
  }, [operations, activeTab, getOperationBaseUrl, navigate]);

  const handleReprendre = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    navigate(`${getOperationBaseUrl(operation)}?tab=1`, { state: { action: 'reprendre' } });
  }, [operations, getOperationBaseUrl, navigate]);

  const handleSigner = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    navigate(`${getOperationBaseUrl(operation)}?tab=3`, { state: { action: 'signer' } });
  }, [operations, getOperationBaseUrl, navigate]);

  const handleAccepter = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    navigate(`${getOperationBaseUrl(operation)}?tab=2`, { state: { action: 'accepter' } });
  }, [operations, getOperationBaseUrl, navigate]);

  // ============================================================================
  // Actions
  // ============================================================================

  const handleRefuserClick = (id: string) => {
    setOperationToRefuse(id);
    setIsRefuseModalOpen(true);
  };

  const handleConfirmRefuse = async () => {
    if (!operationToRefuse) {
      setIsRefuseModalOpen(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/subsequents/${operationToRefuse}/refuser`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Erreur lors du refus de l\'opération');

      // Mise à jour locale de l'état
      const updateStatus = (op: Operation) => {
        if (op.id === operationToRefuse) {
          return {
            ...op,
            status: { ...op.status, nom: 'Refusé', action: false }
          };
        }
        return op;
      };

      setOperations(prev => prev.map(updateStatus));
      setFilteredData(prev => prev.map(updateStatus));
    } catch (error) {
      console.error('Erreur lors du refus:', error);
      alert('Erreur lors du refus de l\'opération.');
    }

    setIsRefuseModalOpen(false);
    setOperationToRefuse(null);
  };

  const handleCancelRefuse = () => {
    setIsRefuseModalOpen(false);
    setOperationToRefuse(null);
  };

  const handleRelancerSignataire = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/subsequents/operation-ms/${id}/relancer-signataire`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Erreur lors de la relance');
      alert('Relance envoyée avec succès');
    } catch (error) {
      console.error('Erreur lors de la relance:', error);
      alert('Une erreur est survenue lors de l\'envoi de la relance');
    }
  };

  const handleExport = useCallback((id: string) => {
    const operationToExport = operations.find(op => op.id === id);
    if (operationToExport) {
      handleExportCSV(
        [operationToExport],
        columns,
        `Operation-${id}-${new Date().toISOString().slice(0, 10)}.csv`
      );
    }
  }, [operations]);

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/operations/exportOperations`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) throw new Error('Erreur lors de l\'exportation');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export-operations-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Une erreur est survenue lors de l\'export des opérations');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownload = async (operation: Operation) => {
    try {
      const externalId = operation.source === 'BCI'
        ? `${operation.departement}${operation.numero || operation.id}F`
        : operation.numero || operation.id;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/documents/signed-contrat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            external_id: externalId,
            external_type: 'operation',
            source: operation.source,
          })
        }
      );

      if (!response.ok) throw new Error('Erreur lors du téléchargement');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const annee = process.env.REACT_APP_ANNEE_EDITION || "25";
      a.download = `${annee}${operation.numero} (signed).pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors du téléchargement du marché signé');
    }
  };

  const downloadArchivePDF = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc-archive/${id}/download`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Erreur téléchargement archive');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `archive-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Impossible de télécharger le fichier PDF.');
    }
  };

  /**
   * Ouvre la popup de confirmation pour supprimer un brouillon
   */
  const handleDeleteBrouillon = (id: string, source: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher le clic sur la ligne
    setOperationToDelete(id);
    setOperationToDeleteSource(source);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  /**
   * Confirme la suppression du brouillon
   */
  const handleConfirmDelete = async () => {
    if (!operationToDelete || isDeleting) {
      setIsDeleteModalOpen(false);
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      // URL différente selon le type d'opération
      const deleteUrl = operationToDeleteSource === 'MS'
        ? `${process.env.REACT_APP_API_URL}/subsequents/operation/${operationToDelete}/delete`
        : `${process.env.REACT_APP_API_URL}/bdc/operation/${operationToDelete}/delete`;

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la suppression du brouillon');
      }

      // Retirer l'opération de la liste locale
      setOperations(prev => prev.filter(op => op.id !== operationToDelete));
      setFilteredData(prev => prev.filter(op => op.id !== operationToDelete));

      // Mettre à jour le total de la pagination
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));

      // Fermer la popup
      setIsDeleteModalOpen(false);
      setOperationToDelete(null);
      setOperationToDeleteSource(null);
      setIsDeleting(false);
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      setDeleteError(error.message || 'Une erreur est survenue lors de la suppression du brouillon.');
      setIsDeleting(false);
    }
  };

  /**
   * Annule la suppression du brouillon
   */
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setOperationToDelete(null);
    setOperationToDeleteSource(null);
    setDeleteError(null);
  };

  // ============================================================================
  // Handlers de navigation et filtres
  // ============================================================================

  const handleTabChange = (tabIndex: number) => {
    if (tabIndex === activeTab) return;

    setLoading(true);
    setError(null);
    setOperations([]);
    setFilteredData([]);
    setDataArchive([]);
    setActiveTab(tabIndex);
    setCurrentPage(1);
    // Réinitialiser les filtres lors du changement d'onglet
    setActiveFilters({});
  };

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    fetchOperations(1, activeTab, value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    if (TABS[activeTab] === 'Archive') {
      fetchArchives({ page });
    } else if (TABS[activeTab] === 'Paniers importés') {
      fetchPaniersImportes(page);
    } else {
      fetchOperations(page, activeTab);
    }
  };

  const handleRowClick = (id: string) => {
    const operation = operations.find(op => op.id === id);
    if (!operation) return;

    const baseUrl = getOperationBaseUrl(operation);
    const userRole = getUserRoleFromStorage();
    const statusId = operation.status?.id || 0;

    if (!statusId) {
      navigate(`${baseUrl}?tab=0`, { state: { action: '' } });
      return;
    }

    const actionMapping = getStatusActionForRole(statusId, userRole, operation.source);
    const statusName = operation.status.nom;
    const source = operation.source;
    let tab = 0;

    // Détermination de l'onglet selon le statut et l'action
    switch (true) {
      case actionMapping.action === 'signer':
        tab = 3;
        break;
      case source === 'BC' && statusName === 'En attente de l\'offre':
      case actionMapping.action === 'reprendre':
        tab = 1;
        break;
      case source === 'BCI' && statusName === 'À accepter':
        tab = 1;
        break;
      case actionMapping.action === 'analyser':
        tab = 2;
        break;
      case source === 'BC' && statusName === 'À analyser':
        tab = 0;
        break;
      case actionMapping.action === 'candidater':
      case actionMapping.action === 'accepter':
        tab = source === 'BC' ? 2 : 1;
        break;
      case statusName === 'En attente de signature prestataire':
        tab = 3;
        break;
      case statusName === 'Candidature en cours':
      case source === 'MS' && statusName === 'Consultation en cours':
      case source === 'BC' && ['Refusé', 'Accepté', 'Validé'].includes(statusName):
        tab = 2;
        break;
      default:
        tab = 0;
    }

    navigate(`${baseUrl}?tab=${tab}`, { state: { action: actionMapping.action } });
  };

  const handleFilter = () => setIsFilterModalOpen(true);

  const handleSelectionChange = (selectedIds: string[]) => setSelectedItems(selectedIds);

  // ============================================================================
  // Recherche
  // ============================================================================

  const handleSearch = async (query: string) => {
    setSearchLoading(true);
    try {
      if (!query.trim()) {
        // Recharger les données sans filtre de recherche
        if (TABS[activeTab] === 'Paniers importés') {
          await fetchPaniersImportes(1);
        } else {
          await fetchOperations(1, activeTab, selectedType);
        }
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      // Adapter l'endpoint selon l'onglet actif
      const isPaniersImportes = TABS[activeTab] === 'Paniers importés';
      const endpoint = isPaniersImportes
        ? `${process.env.REACT_APP_API_URL}/bdci/operations?operation_nom=${encodeURIComponent(query)}`
        : `${process.env.REACT_APP_API_URL}/operation?operation_nom=${encodeURIComponent(query)}`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erreur lors de la recherche');

      const data = await response.json();

      if (data.status === 'success') {
        // Transformer selon le type de données
        const formattedData = isPaniersImportes
          ? data.data.map(transformBciOperationToDisplay)
          : transformApiOperationsToDisplay(data.data);

        // Trier les paniers importés par priorité si nécessaire
        const finalData = isPaniersImportes
          ? sortPaniersImportesByPriority(formattedData)
          : formattedData;

        setOperations(finalData);
        setFilteredData(finalData);
        setPagination({
          currentPage: data.pagination?.current_page || 1,
          lastPage: data.pagination?.last_page || Math.ceil(finalData.length / itemsPerPage),
          perPage: data.pagination?.per_page || itemsPerPage,
          total: data.pagination?.total || finalData.length
        });
      } else {
        setOperations([]);
        setFilteredData([]);
        setPagination({ currentPage: 1, lastPage: 1, perPage: itemsPerPage, total: 0 });
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setError('Erreur lors de la recherche. Veuillez réessayer.');
    } finally {
      setSearchLoading(false);
    }
  };

  // ============================================================================
  // Filtres
  // ============================================================================

  const maxPrice = useMemo(
    () => Math.max(...operations.map(op => op.prixTotal || 0), 0),
    [operations]
  );

  /**
   * Vérifie si des filtres sont actuellement actifs
   * Prend en compte TOUS les filtres possibles
   */
  const hasActiveFilters = useMemo(() => {
    // Vérification de chaque filtre individuellement pour plus de clarté
    const hasLotTechnique = !!activeFilters.lotTechnique;
    const hasLotGeographique = !!activeFilters.lotGeographique;
    const hasNomOperation = !!activeFilters.nomOperation;
    const hasEntite = !!activeFilters.entite;
    const hasCreateur = !!activeFilters.createur;
    const hasStatus = !!activeFilters.status;
    const hasType = !!(activeFilters.type && activeFilters.type !== 'Tous' && activeFilters.type !== '');
    const hasPrixMin = !!(activeFilters.prixRange && activeFilters.prixRange.min > 0);
    const hasPrixMax = !!(activeFilters.prixRange && activeFilters.prixRange.max > 0);
    const hasDateStart = !!(activeFilters.dateRange && activeFilters.dateRange.start);
    const hasDateEnd = !!(activeFilters.dateRange && activeFilters.dateRange.end);

    return (
      hasLotTechnique ||
      hasLotGeographique ||
      hasNomOperation ||
      hasEntite ||
      hasCreateur ||
      hasStatus ||
      hasType ||
      hasPrixMin ||
      hasPrixMax ||
      hasDateStart ||
      hasDateEnd
    );
  }, [activeFilters]);

  /**
   * Applique les filtres en appelant l'API avec les paramètres transformés
   * Les filtres côté serveur utilisent les QueryFilters (OperationFilter.php)
   */
  const handleFilterApply = async (filters: FilterOptions) => {
    // Fermer le modal immédiatement
    setIsFilterModalOpen(false);

    // Sauvegarder les filtres actifs
    setActiveFilters(filters);

    // Réinitialiser la page à 1 lors de l'application de nouveaux filtres
    setCurrentPage(1);

    // Charger les données avec les nouveaux filtres selon l'onglet actif
    if (TABS[activeTab] === 'Paniers importés') {
      await fetchPaniersImportesWithFilters(1, filters);
    } else {
      await fetchOperationsWithFilters(1, activeTab, selectedType, filters);
    }
  };

  /**
   * Réinitialise tous les filtres et recharge les données
   */
  const handleClearFilters = async () => {
    setActiveFilters({});
    setCurrentPage(1);
    // IMPORTANT: Passer explicitement {} car setActiveFilters est asynchrone
    // et activeFilters n'est pas encore mis à jour à ce stade
    if (TABS[activeTab] === 'Paniers importés') {
      await fetchPaniersImportesWithFilters(1, {});
    } else {
      await fetchOperationsWithFilters(1, activeTab, selectedType, {});
    }
  };

  // ============================================================================
  // Fonctions de récupération des données
  // ============================================================================

  /**
   * Récupère les opérations avec support des filtres
   * @param page - Page courante
   * @param tab - Onglet actif (détermine les filtres de statut)
   * @param type - Type d'opération (BC/MS/Tous)
   * @param filters - Filtres optionnels de l'interface utilisateur
   */
  const fetchOperationsWithFilters = async (
    page = 1,
    tab = activeTab,
    type = selectedType,
    filters: Partial<FilterOptions> = {}
  ) => {
    // Utiliser le contrôleur existant s'il n'est pas annulé, sinon en créer un nouveau
    let controller = abortControllerRef.current;
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      abortControllerRef.current = controller;
    }

    setOperations([]);
    setFilteredData([]);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      // Construire les paramètres API à partir des filtres UI
      const apiFilters = transformFiltersToApiParams(filters, page, 100);

      // Ajouter les filtres par onglet (statut)
      if (tab !== 0 && tab < TABS.length) {
        const tabMapping = TAB_TO_STATUS_MAPPING[tab];
        if (tabMapping?.filter) {
          // Parser les filtres de l'onglet et les ajouter
          const tabFilters = tabMapping.filter.split('&&');
          tabFilters.forEach(f => {
            const [key, value] = f.split('=');
            if (key && value) {
              (apiFilters as any)[key] = value;
            }
          });
        }
      }

      // Filtre par type (BC/MS)
      if (type !== 'Tous') {
        apiFilters.type = type;
      }

      // Construire l'URL avec les paramètres
      const queryString = buildQueryString(apiFilters);

      console.log('Filtres API envoyés:', apiFilters);
      console.log('Query string:', queryString);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/operation?${queryString}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal,
        }
      );

      if (controller.signal.aborted) return;
      if (!response.ok) throw new Error(`Erreur: ${response.statusText}`);

      const responseData = await response.json();

      if (responseData.status !== 'success' || !Array.isArray(responseData.data)) {
        throw new Error('Format de réponse API inattendu');
      }

      // Pagination
      if (responseData.pagination) {
        setPagination({
          currentPage: responseData.pagination.current_page || 1,
          lastPage: responseData.pagination.last_page || 1,
          perPage: responseData.pagination.per_page || 50,
          total: responseData.pagination.total || 0
        });
      }

      // Transformation des données
      const formattedData = transformApiOperationsToDisplay(responseData.data);

      // Filtrer les brouillons de l'onglet "En attente"
      let finalData = tab === 2
        ? formattedData.filter(op => op.status.id !== 1)
        : formattedData;

      // Appliquer les filtres locaux pour les champs non supportés par l'API
      // (ex: dateRange, createur qui nécessitent des jointures complexes)
      if (filters.dateRange?.start && filters.dateRange?.end) {
        finalData = finalData.filter(op => {
          const opDate = new Date(op.dateCreation.split('/').reverse().join('-'));
          return opDate >= filters.dateRange!.start! && opDate <= filters.dateRange!.end!;
        });
      }

      if (filters.createur) {
        finalData = finalData.filter(op =>
          op.creePar.toLowerCase().includes(filters.createur!.toLowerCase())
        );
      }

      // Filtre sur entite (organisme pour prestataire, prestataire pour acheteur)
      if (filters.entite) {
        finalData = finalData.filter(op => {
          // Pour les acheteurs: filtrer sur prestataireNom
          // Pour les prestataires: filtrer sur organismeNom
          const nomAComparer = isClientView ? op.prestataireNom : op.organismeNom;
          return nomAComparer === filters.entite;
        });
      }

      setFilteredData(finalData);
      setOperations(finalData);
      updateNotifications(formattedData);

    } catch (err: any) {
      if (err.name === "AbortError") return;
      console.error("Erreur de chargement:", err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données.');
    }
  };

  /**
   * Fonction simplifiée pour charger les opérations sans filtres personnalisés
   */
  const fetchOperations = async (page = 1, tab = activeTab, type = selectedType) => {
    // Utiliser les filtres actifs s'ils existent
    await fetchOperationsWithFilters(page, tab, type, activeFilters);
  };

  const fetchArchives = async (params: Record<string, any> = {}) => {
    // Utiliser le contrôleur existant s'il n'est pas annulé, sinon en créer un nouveau
    let controller = abortControllerRef.current;
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      abortControllerRef.current = controller;
    }

    try {
      const queryParams = { ...params, per_page: '100' };
      const query = new URLSearchParams(queryParams).toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc-archive?${query}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal,
        }
      );

      if (controller.signal.aborted) return;

      const result = await response.json();

      if (result.status === 'success') {
        setDataArchive(result.data || []);
        setPagination({
          currentPage: result.pagination.current_page,
          lastPage: result.pagination.last_page,
          perPage: result.pagination.per_page,
          total: result.pagination.total
        });
      } else {
        setDataArchive([]);
      }
    } catch (error: any) {
      if (error.name === "AbortError") return;
      console.error('Erreur réseau Archives:', error);
      setDataArchive([]);
    }
  };

  const fetchPaniersImportes = async (page = 1) => {
    // Utiliser les filtres actifs s'ils existent
    await fetchPaniersImportesWithFilters(page, activeFilters);
  };

  /**
   * Récupère les paniers importés (BDCI) avec support des filtres
   */
  const fetchPaniersImportesWithFilters = async (
    page = 1,
    filters: Partial<FilterOptions> = {}
  ) => {
    // Utiliser le contrôleur existant s'il n'est pas annulé, sinon en créer un nouveau
    let controller = abortControllerRef.current;
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      abortControllerRef.current = controller;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      // Construire les paramètres de requête
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('per_page', '100');

      // Ajouter les filtres API
      if (filters.nomOperation) {
        params.append('operation_nom', filters.nomOperation);
      }
      if (filters.lotTechnique) {
        // Extraire uniquement le code du lot technique (ex: "202" de "202 - Gros œuvre")
        const lotCode = filters.lotTechnique.split(' - ')[0].trim();
        params.append('operation_lot_technique', lotCode);
      }
      if (filters.lotGeographique) {
        params.append('operation_departement', filters.lotGeographique);
      }
      if (filters.entite) {
        params.append('operation_client', filters.entite);
      }
      if (filters.status) {
        params.append('operation_status', filters.status);
      }
      if (filters.prixRange?.min && filters.prixRange.min > 0) {
        params.append('operation_prix_total_min', filters.prixRange.min.toString());
      }
      if (filters.prixRange?.max && filters.prixRange.max > 0) {
        params.append('operation_prix_total_max', filters.prixRange.max.toString());
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdci/operations?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal,
        }
      );

      if (controller.signal.aborted) return;

      const result = await response.json();

      if (result.status === 'success') {
        const formattedData = result.data.map(transformBciOperationToDisplay);
        let finalData = sortPaniersImportesByPriority(formattedData);

        // Appliquer les filtres locaux pour les champs non supportés par l'API
        if (filters.dateRange?.start && filters.dateRange?.end) {
          finalData = finalData.filter((op: Operation) => {
            const opDate = new Date(op.dateCreation.split('/').reverse().join('-'));
            return opDate >= filters.dateRange!.start! && opDate <= filters.dateRange!.end!;
          });
        }

        if (filters.createur) {
          finalData = finalData.filter((op: Operation) =>
            op.creePar.toLowerCase().includes(filters.createur!.toLowerCase())
          );
        }

        // Filtre sur entite (organisme pour prestataire, prestataire pour acheteur)
        if (filters.entite) {
          finalData = finalData.filter((op: Operation) => {
            const nomAComparer = isClientView ? op.prestataireNom : op.organismeNom;
            return nomAComparer === filters.entite;
          });
        }

        setOperations(finalData);
        setFilteredData(finalData);

        // Compteur des paniers à accepter
        const countAAccepter = result.data.filter(
          (op: any) => op.status?.id === 4 && op.status?.action === true
        ).length;
        setPaniersImportesCount(countAAccepter);

        setPagination({
          currentPage: result.pagination?.current_page || page,
          lastPage: result.pagination?.last_page || 1,
          perPage: result.pagination?.per_page || 50,
          total: result.pagination?.total || finalData.length
        });
      } else {
        setOperations([]);
        setFilteredData([]);
        setPaniersImportesCount(0);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      console.error('Erreur réseau Paniers importés:', error);
      setOperations([]);
      setFilteredData([]);
      setPaniersImportesCount(0);
    }
  };

  const fetchActionRequiseCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/operations-compteur?status_action=1`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (result.status === 'success' && result.data) {
        const count = result.data.count ?? 0;
        const countBDCI = result.data.count_bdci ?? 0;
        const totalCount = count + countBDCI;

        setActionRequiseCount(count);
        setUnreadOperations(totalCount);
        setUnreadLocalOperations(totalCount);
        onNotificationUpdate?.(totalCount);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du compteur:', err);
    }
  };

  const fetchPaniersImportesCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/operations-compteur?status_action=1`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (result.status === 'success' && result.data) {
        const countBDCI = result.data.count_bdci ?? 0;
        setPaniersImportesCount(countBDCI);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du compteur des paniers importés:', err);
    }
  };

  const updateNotifications = (operations: Operation[]) => {
    try {
      const count = operations.filter(op => op.actionRequise).length;
      setUnreadLocalOperations(count);

      if (typeof setUnreadOperations === 'function') {
        setUnreadOperations(count);
      }

      onNotificationUpdate?.(count);
    } catch (error) {
      console.error("Erreur lors de la mise à jour des notifications:", error);
    }
  };

  // ============================================================================
  // Notifications pour TabNav
  // ============================================================================

  const notifications = useMemo(() => [
    { tabIndex: TABS.indexOf('Tous'), count: actionRequiseCount },
    { tabIndex: TABS.indexOf('Action requise'), count: actionRequiseCount },
    { tabIndex: TABS.indexOf('Paniers importés'), count: paniersImportesCount }
  ].filter(n => n.tabIndex !== -1 && n.count > 0), [actionRequiseCount, paniersImportesCount]);

  // ============================================================================
  // Colonnes du tableau
  // ============================================================================

  // Colonnes pour les archives
  const archiveColumns = useMemo(() => [
    {
      field: 'type',
      header: 'Type',
      renderCell: () => <div className="type-info">BC</div>
    },
    {
      field: 'date_commande',
      header: 'Date',
      renderCell: (value: any) => <div>{formatDate(value, false)}</div>
    },
    {
      field: 'panier',
      header: 'Nom',
      renderCell: (value: string, row: any) => <div>{row.panier || '-'}</div>
    },
    {
      field: 'entite',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: any, row: any) => <div>{row.entite?.nom || '-'}</div>
    },
    {
      field: 'total_ht',
      header: 'Total HT',
      renderCell: (value: number, row: any) => <div>{formatPrice(Number(row?.total_ht))}</div>
    },
    {
      field: 'statut',
      header: 'Statut',
      renderCell: (value: string) => (
        <div className={`status-badge ${toCssClass(value)}`}>
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </div>
      )
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: any, row: any) => (
        <Button
          value="PDF"
          variant="secondary"
          type="button"
          onClick={() => downloadArchivePDF(row.id)}
          className="action-button"
        />
      )
    }
  ], []);

  // Fonction de rendu de statut réutilisable
  const renderStatus = useCallback((value: ApiStatus, row: Operation) => {
    if (!value) return null;

    const statusClass = toCssClass(value.nom);
    const actionClass = value.action ? 'action-requise' : '';

    // Renommage selon le type d'opération
    let displayNom = value.nom;
    if (row.source === 'MS') {
      if (value.id === 6) displayNom = 'Refusé';
      if (value.id === 11) displayNom = 'Accepté';
      if (value.id === 12) displayNom = 'Expiré';
    }
    if (row.source === 'BC') {
      if (value.id === 8) displayNom = 'Refusé';
      if (value.id === 7) displayNom = 'Accepté';
      if (value.id === 9) displayNom = 'Expiré';
    }

    return (
      <div
        className={`status-badge ${statusClass} ${actionClass}`}
        title={`Type: ${value.type_compte}`}
      >
        {displayNom}
      </div>
    );
  }, []);

  // Fonction de rendu des actions réutilisable
  const renderActions = useCallback((row: Operation) => {
    if (!row) return null;

    const statusId = row.status?.id || 0;
    const isActionRequired = row.actionRequise;
    const userRole = getUserRoleFromStorage();
    const actionMapping = getStatusActionForRole(statusId, userRole, row.source);

    const statusClass = toCssClass(row.status?.nom || '');

    const handleActionClick = () => {
      switch (actionMapping.action) {
        case 'reprendre': handleReprendre(row.id); break;
        case 'analyser': handleAnalyser(row.id); break;
        case 'candidater': handleCandidater(row.id); break;
        case 'signer': handleSigner(row.id); break;
        case 'export': handleExport(row.id); break;
        case 'download': handleDownload(row); break;
        case 'relancer': handleRelancerSignataire(row.id); break;
        case 'accepter': handleAccepter(row.id); break;
        default: console.log(`Aucune action définie pour le statut ${statusId}`);
      }
    };

    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {actionMapping.showButton && (
          <Button
            value={actionMapping.buttonText}
            variant="secondary"
            size="small"
            onClick={handleActionClick}
            className={`action-button ${statusClass} ${isActionRequired ? 'action-requise' : ''}`}
            disabled={actionMapping.disableButton}
          />
        )}
        {statusId === 1 && (
          <button
            className="delete-brouillon-btn"
            onClick={(e) => handleDeleteBrouillon(row.id, row.source, e)}
            title="Supprimer le brouillon"
          >
            ×
          </button>
        )}
      </div>
    );
  }, [handleReprendre, handleAnalyser, handleCandidater, handleSigner, handleExport, handleAccepter, handleDeleteBrouillon]);

  // Colonnes pour les paniers importés
  const paniersImportesColumns = useMemo(() => [
    {
      field: 'numero',
      header: 'N°',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'source',
      header: 'Type',
      renderCell: (value: string) => (
        <div className="type-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'dateCreation',
      header: 'Date',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'lotTechniqueDisplay',
      header: 'Lot Technique',
      renderCell: (value: string) => (
        <div className="lot-technique text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'departement',
      header: 'Dép',
      renderCell: (value: string) => (
        <div className="lot-geographique text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'nom',
      header: 'Nom',
      renderCell: (value: string) => (
        <div className="operation-nom text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'prixTotal',
      header: 'Total HT',
      renderCell: (value: number) => (
        <div className="montant-operation">
          {value != null ? `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '0,00 €'}
        </div>
      )
    },
    {
      field: 'status',
      header: 'Statut',
      renderCell: renderStatus
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: string, row: Operation) => renderActions(row)
    }
  ], [renderStatus, renderActions]);

  // Colonnes principales pour les opérations
  const columns = useMemo(() => [
    {
      field: 'numero',
      header: 'N°',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'source',
      header: 'Type',
      renderCell: (value: string) => (
        <div className="type-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'dateCreation',
      header: 'Date',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'lotTechniqueDisplay',
      header: 'Lot Technique',
      renderCell: (value: string) => (
        <div className="lot-technique text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'departement',
      header: 'Dép',
      renderCell: (value: string) => (
        <div className="lot-geographique text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'creePar',
      header: 'Créé par',
      renderCell: (value: string) => (
        <div className="user-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'nom',
      header: 'Nom',
      renderCell: (value: string) => (
        <div className="operation-nom text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'prestataireNom',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: string, row: Operation) => {
        let displayValue: string;
        if (isClientView) {
          // Acheteur: afficher prestataireNom ou 'Prestataire non défini'
          displayValue = row.source === 'BCI' ? row.nom : (row.prestataireNom && row.prestataireNom !== 'Non défini' ? row.prestataireNom : '-');
        } else {
          // Prestataire: afficher organismeNom ou 'Client non défini'
          displayValue = row.organismeNom && row.organismeNom !== 'Non défini' ? row.organismeNom : 'Client non défini';
        }
        return (
          <div className="client-info text-truncate" title={displayValue}>{displayValue}</div>
        );
      }
    },
    {
      field: 'prixTotal',
      header: 'Total HT',
      renderCell: (value: number) => (
        <div className="montant-operation">
          {value != null ? `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '0,00 €'}
        </div>
      )
    },
    {
      field: 'status',
      header: 'Statut',
      renderCell: renderStatus
    },
    {
      field: 'tempsRestant',
      header: 'Temps restant',
      renderCell: (value: string, row: Operation) => {
        if (!row || !row.expireAt) return null;
        // Masquer pour certains statuts spécifiques
        // BC avec status id=2 (En attente de l'offre) : pas de chrono
        // Tous les types avec status id=9 (Expiré) : pas de chrono
        if ((row.source === 'BC' && row.status.id === 2) || row.status.id === 9) return null;
        return <CompteARebourd targetDate={row.expireAt} />;
      }
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: string, row: Operation) => renderActions(row)
    }
  ], [isClientView, renderStatus, renderActions]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Mise à jour des notifications quand actionRequiseCount change
  useEffect(() => {
    setUnreadOperations(actionRequiseCount);
    setUnreadLocalOperations(actionRequiseCount);
    onNotificationUpdate?.(actionRequiseCount);
  }, [actionRequiseCount, setUnreadOperations, onNotificationUpdate]);

  // Chargement des données au montage et changement d'onglet/type/page
  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      setLoading(true);
      
      try {
        if (TABS[activeTab] === 'Archive') {
          await fetchArchives({ page: currentPage, statut: TAB_TO_STATUS_MAPPING[activeTab].filter });
        } else if (TABS[activeTab] === 'Paniers importés') {
          await fetchPaniersImportes(currentPage);
        } else {
          await fetchOperations(currentPage, activeTab, selectedType);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    fetchActionRequiseCount();
    fetchPaniersImportesCount();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, selectedType, currentPage]);

  // ============================================================================
  // Rendu
  // ============================================================================

  return (
    <div className="commandes-page">
      <section className="tableau-de-bord-section">
        <div className="commandes-header">
          <h1>Commandes</h1>

          <div className="commandes-actions">
            <Button
              value="Nouveau Bon de Commande"
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setIsBonCommandeModalOpen(true)}
            />
            <Button
              value="Nouveau Marché Subséquent"
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setIsLancerMarcheModalOpen(true)}
            />
          </div>
        </div>

        <TabNav
          tabs={[...TABS]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          notifications={notifications}
          rightAlignedTabs={['Archive', 'Paniers importés']}
        />

        <div className="button-group">
          <div className="button-filter-group">
            <SearchBar
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              placeholder="Trouver une commande"
              isLoading={searchLoading}
            />
            <Button
              value="exporter"
              variant="third"
              size="small"
              icon={<ExportIcon />}
              onClick={handleExportAll}
              disabled={isExporting}
            />
            <div className="filter-button-container">
              <Button
                value="filtrer"
                variant={hasActiveFilters ? "primary" : "third"}
                size="small"
                icon={<FilterIcon />}
                onClick={handleFilter}
              />
              {hasActiveFilters && (
                <button
                  className="filter-reset-button"
                  onClick={handleClearFilters}
                  title="Réinitialiser les filtres"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Chargement des opérations en cours...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>Erreur: {error}</p>
          </div>
        ) : (
          <TableauDeBord
            data={TABS[activeTab] === 'Archive' ? dataArchive : filteredData}
            columns={
              TABS[activeTab] === 'Archive'
                ? archiveColumns
                : TABS[activeTab] === 'Paniers importés'
                  ? paniersImportesColumns
                  : columns
            }
            onSearch={handleSearch}
            typeFilters={true}
            onExport={() => handleExportCSV(
              filteredData,
              columns as any,
              `Operations-${new Date().toISOString().slice(0, 10)}.csv`
            )}
            onRowClick={handleRowClick}
            isSelectable={true}
            showActionBar={true}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
            showActionsColumn={false}
            itemsPerPage={pagination.perPage}
            currentPage={pagination.currentPage}
            totalPages={pagination.lastPage}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
          />
        )}
      </section>

      {/* Modal Marché Subséquent */}
      <LancerMarcheModal
        isOpen={isLancerMarcheModalOpen}
        onClose={() => setIsLancerMarcheModalOpen(false)}
        title="Lancer un marché subséquent"
        showNomOperation={true}
        showLotTechnique={true}
        showLotGeographique={true}
        showDepartement={false}
        mode="MS"
      />

      {/* Modal Bon de Commande */}
      <LancerMarcheModal
        isOpen={isBonCommandeModalOpen}
        onClose={() => setIsBonCommandeModalOpen(false)}
        title="Nouveau Bon de Commande"
        showNomOperation={true}
        showLotTechnique={true}
        showLotGeographique={true}
        showDepartement={false}
        mode="BC"
      />

      {/* Modal Filtres */}
      <FiltreDeCommande
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleFilterApply}
        data={operations}
        maxPrice={maxPrice}
        activeFilters={activeFilters}
      />

      {/* Modal Suppression Brouillon */}
      <ValidationPopUp
        isOpen={isDeleteModalOpen}
        title="Supprimer le brouillon"
        text={deleteError || "Êtes-vous sûr de vouloir supprimer ce brouillon ? Cette action est irréversible."}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        cancelLabel="Annuler"
        confirmLabel="Supprimer"
        isLoading={isDeleting}
      />
    </div>
  );
}

export default OperationPage;
